// Copyright (c) Zammo Inc.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import './App.css';
import { createTheme, initializeIcons, loadTheme } from '@fluentui/react';

import Header from './components/Header';
import TokenModal from './components/TokenModal/indext';
import { canUrlCanBeFetchedInIframe, getSimulatorSession, getUrl, SimulatorSession } from './app/actions';
import Simulator from './components/Simulator';
import { htmlLinksAbsolutify, isIOS } from './utils/helpers';
import VoiceOnlySimulator from './components/VoiceOnlySimulator/VoiceOnlySimulator';
import VoiceWelcomeModal from './components/VoiceOnlySimulator/VoiceWelcomeModal';
import SessionFailedModal from './components/Modals/SessionFailedModal';
import TtsAvatarOnlySimulator from './components/TtsAvatarOnlySimulator/TtsAvatarOnlySimulator';

enum SimulatorMode {
  Chat = 'Chat',
  Voice = 'Voice',
  TtsAvatar = 'TtsAvatar',
}

const FakeIframe = styled.div`
  width: 100%;
  border: none;
  height: calc(100vh - 64px);
`;

const Iframe = styled.iframe`
  width: 100%;
  border: none;
  height: calc(100vh - 64px);
`;

initializeIcons('https://res.cdn.office.net/files/fabric-cdn-prod_20241209.001/assets/icons/');

loadTheme(
  createTheme({
    defaultFontStyle: { fontFamily: 'Montserrat', fontWeight: 'normal' },
  })
);

const App = () => {
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showFakeIframe, setShowFakeIframe] = useState(false);
  const [showVoiceSimulator, setShowVoiceSimulator] = useState(false);
  const [isSessionFailed, setIsSessionFailed] = useState(false);
  const [simulatorSession, setSimulatorSession] = useState<SimulatorSession>();
  const [isLoading, setIsLoading] = useState(false);
  const [simulatorToken, setSimulatorToken] = useState('');
  const [url, setUrl] = useState('');
  const [fakeIframeContent, setFakeIframeContent] = useState('');
  const location = useLocation();
  const [simulatorMode, setSimulatorMode] = useState<SimulatorMode>(SimulatorMode.Voice);

  useEffect(() => {
    const queries = queryString.parse(location.search);
    if (queries.token) {
      setSimulatorToken(`${queries.token}`);
    } else {
      setShowTokenModal(true);
    }
    if (queries.channel === 'voice') {
      setSimulatorMode(SimulatorMode.Voice);
    } else if (queries.channel === 'tts-avatar') {
      setSimulatorMode(SimulatorMode.TtsAvatar);
    } else {
      setSimulatorMode(SimulatorMode.Chat); // default to chat
    }

    if (queries.url) {
      setUrl(`${queries.url}`);
    }
  }, [location]);

  useEffect(() => {
    if (simulatorToken && simulatorMode !== SimulatorMode.TtsAvatar) {
      getSimulatorSession(simulatorToken)
        .then((data: SimulatorSession) => {
          setSimulatorSession(data);
        })
        .catch(() => {
          setIsSessionFailed(true);
        });
    }
  }, [simulatorToken]);

  useEffect(() => {
    try {
      if (url) {
        setIsLoading(true);
        canUrlCanBeFetchedInIframe(url).then((res) => {
          if (!res) {
            getUrl(url).then((data) => {
              const cleanedHtmlContent = data
                .replace(new RegExp('\\\\"', 'g'), '"')
                .replace(/\\n/g, '')
                .replace(/\\t/g, '')
                .replace(/\\\\x22/g, '"');

              const htmlWithUrlFixed = htmlLinksAbsolutify(cleanedHtmlContent, url);
              setShowFakeIframe(true);
              setFakeIframeContent(htmlWithUrlFixed);
              setIsLoading(false);
            });
          } else {
            setIsLoading(false);
          }
        });
      }
    } catch (err) {
      setIsLoading(false);
      setShowFakeIframe(false);
      setFakeIframeContent('');
    }
  }, [url]);

  return (
    <>
      <Header setURL={setUrl} isShowURLInput={simulatorMode === SimulatorMode.Chat && !url} />
      {!isLoading && url && !showFakeIframe && <Iframe src={url} />}
      {!isLoading && url && showFakeIframe && (
        <FakeIframe
          dangerouslySetInnerHTML={{
            __html: fakeIframeContent,
          }}
        />
      )}

      {simulatorToken && simulatorSession && simulatorMode === SimulatorMode.Chat && (
        <Simulator simulatorToken={simulatorToken} session={simulatorSession} />
      )}

      {simulatorToken &&
        simulatorSession &&
        simulatorMode === SimulatorMode.Voice &&
        (showVoiceSimulator || isIOS()) && (
          <VoiceOnlySimulator
            simulatorToken={simulatorToken}
            directLineToken={simulatorSession.directLineToken}
            speechToken={simulatorSession.speechToken}
            speechRegion={simulatorSession.speechRegion}
            speechVoiceNames={simulatorSession.speechVoiceNames}
          />
        )}

      {!simulatorToken && <TokenModal isOpen={showTokenModal} onDismiss={() => setShowTokenModal(false)} />}

      <VoiceWelcomeModal
        isOpen={!!simulatorSession && simulatorMode === SimulatorMode.Voice && !showVoiceSimulator}
        onConfirm={() => setShowVoiceSimulator(true)}
      />

      {simulatorMode === SimulatorMode.TtsAvatar && (
        <TtsAvatarOnlySimulator zammoApiBaseUrl={window.ZAMMO_API_URL} simulatorToken={simulatorToken} />
      )}

      <SessionFailedModal isOpen={isSessionFailed} />
    </>
  );
};

export default App;
